import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useAuth } from "@/contexts/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { safeAreaTop, safeAreaX } from "@/utils/viewport";

const NAV_LINKS = [
  { label: "Features", path: "/features" },
  { label: "Playground", path: "/playground" },
  { label: "Pricing", path: "/pricing" },
  { label: "Docs", path: "/docs" },
  { label: "Blog", path: "/blog" },
];

export default function MarketingNav() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const isLight = theme.palette.mode === "light";

  return (
    <>
      <AppBar
        component="header"
        position="sticky"
        elevation={0}
        sx={{
          ...safeAreaTop,
          ...safeAreaX,
          bgcolor: isLight ? "rgba(255,255,255,0.85)" : "rgba(15,11,46,0.85)",
          backdropFilter: "blur(12px)",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 4 } }}>
          {/* Logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textDecoration: "none",
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt="PDFGlyph"
              sx={{ width: 28, height: 28 }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "text.primary" }}
            >
              PDFGlyph
            </Typography>
          </Box>

          {/* Desktop nav */}
          {!isMobile && (
            <Box
              component="nav"
              aria-label="Primary"
              sx={{ display: "flex", gap: 3, alignItems: "center" }}
            >
              {NAV_LINKS.map((link) => (
                <Typography
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    fontSize: 14,
                    fontWeight: 500,
                    "&:hover": { color: "text.primary" },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
            </Box>
          )}

          {/* Right side */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ThemeToggle />
            {isMobile ? (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                aria-label="Open navigation menu"
                aria-haspopup="true"
                aria-expanded={drawerOpen}
                aria-controls={drawerOpen ? "marketing-mobile-nav" : undefined}
              >
                <MenuIcon />
              </IconButton>
            ) : isAuthenticated ? (
              <Button
                component={RouterLink}
                to="/dashboard"
                sx={{
                  borderRadius: 24,
                  background: isLight
                    ? "linear-gradient(135deg, #1d4ed8, #a78bfa)"
                    : "linear-gradient(135deg, #a78bfa, #60a5fa)",
                  color: isLight ? "white" : "#0f0b2e",
                  fontWeight: 600,
                  px: 2.5,
                  "&:hover": { opacity: 0.9 },
                }}
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  component={RouterLink}
                  to="/login"
                  sx={{ color: "primary.main", fontWeight: 600 }}
                >
                  Log in
                </Button>
                <Button
                  component={RouterLink}
                  to="/register"
                  sx={{
                    borderRadius: 24,
                    background: isLight
                      ? "linear-gradient(135deg, #1d4ed8, #a78bfa)"
                      : "linear-gradient(135deg, #a78bfa, #60a5fa)",
                    color: isLight ? "white" : "#0f0b2e",
                    fontWeight: 600,
                    px: 2.5,
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  Get started free
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <List
            id="marketing-mobile-nav"
            component="nav"
            aria-label="Primary mobile"
          >
            {NAV_LINKS.map((link) => (
              <ListItemButton
                key={link.path}
                component={RouterLink}
                to={link.path}
                onClick={() => setDrawerOpen(false)}
              >
                <ListItemText primary={link.label} />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ px: 2, py: 1 }}>
            <ThemeToggle />
          </Box>
          {isAuthenticated ? (
            <Button
              component={RouterLink}
              to="/dashboard"
              fullWidth
              variant="contained"
              sx={{ borderRadius: 24, mt: 1 }}
            >
              Dashboard
            </Button>
          ) : (
            <>
              <Button
                component={RouterLink}
                to="/login"
                fullWidth
                sx={{ mt: 1 }}
              >
                Log in
              </Button>
              <Button
                component={RouterLink}
                to="/register"
                fullWidth
                variant="contained"
                sx={{ borderRadius: 24, mt: 1 }}
              >
                Get started free
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
}
